import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, Button, message, List, Empty, Modal, Upload, Alert, Tag, Divider, Descriptions, Space, Typography, Radio } from 'antd';
import { UploadOutlined, CheckCircleOutlined, SearchOutlined, ArrowLeftOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import PermissionWrapper from '../../components/security/PermissionWrapper';
import verificationService, { getVerificationList, searchEnterprise, queryEnterpriseByOcr } from '../../services/verification';
import authService from '../../services/auth';
import tianaiCaptcha from '../../services/captcha';
import { recognizeIdCard, recognizeBusinessLicense } from '../../services/ocr';
import dayjs from 'dayjs';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './IdentityVerificationPage.css';

const { Text } = Typography;

/**
 * 实名认证页面
 * 根据认证状态自动切换布局：
 * - State A：未认证 → 仅显示个人认证表单
 * - State B：已个人认证 → 显示认证信息卡片 + 企业认证区域
 * - State C：已个人+企业认证 → 显示所有认证信息 + 添加更多企业认证
 */
const IdentityVerificationPage = () => {
  const { isMobile } = useBreakpoint();
  const [personalForm] = Form.useForm();
  const [enterpriseForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsSendInterval, setSmsSendInterval] = useState(60);

  // 认证状态
  const [personalVerification, setPersonalVerification] = useState(null);
  const [enterpriseVerifications, setEnterpriseVerifications] = useState([]);
  const [verificationListLoading, setVerificationListLoading] = useState(true);
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);

  // 人机验证状态
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');

  // OCR状态
  const [personalIdCardOcrUploading, setPersonalIdCardOcrUploading] = useState(false);
  const [businessLicenseOcrUploading, setBusinessLicenseOcrUploading] = useState(false);
  const [legalPersonIdCardOcrUploading, setLegalPersonIdCardOcrUploading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [pendingSubmitType, setPendingSubmitType] = useState(null);

  // 企业认证两步式状态
  const [enterpriseStep, setEnterpriseStep] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);

  // 身份证录入方式
  const [idCardInputMethod, setIdCardInputMethod] = useState('manual');
  const [ocrIdCardName, setOcrIdCardName] = useState(null);

  // ==================== 加载认证列表 ====================

  const loadVerificationList = async () => {
    try {
      setVerificationListLoading(true);
      const res = await getVerificationList();
      const list = Array.isArray(res.data) ? res.data : (res.data?.records || []);
      setPersonalVerification(list.find(v => v.verificationType === 'P') || null);
      setEnterpriseVerifications(list.filter(v => v.verificationType === 'E'));
    } catch (error) {
      console.error('加载认证列表失败:', error);
      message.error(error.message || '加载认证列表失败');
    } finally {
      setVerificationListLoading(false);
    }
  };

  // 页面挂载时加载认证列表
  useEffect(() => {
    loadVerificationList();
  }, []);

  // 验证码倒计时
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 获取验证码配置
  useEffect(() => {
    authService.getCaptchaConfig()
      .then(res => {
        if (res?.data?.smsSendInterval) {
          setSmsSendInterval(res.data.smsSendInterval);
        }
      })
      .catch(err => {
        console.error('获取验证码配置失败:', err);
      });
  }, []);

  // ==================== 工具函数 ====================

  /** 身份证号脱敏：显示前3位和后4位 */
  const maskIdCard = (idCard) => {
    if (!idCard || idCard.length < 8) return idCard;
    return idCard.slice(0, 3) + '****' + idCard.slice(-4);
  };

  /** 重置企业认证表单状态 */
  const resetEnterpriseState = () => {
    setEnterpriseStep(1);
    setSelectedEnterprise(null);
    setSearchResults([]);
    setSearchKeyword('');
    setIdCardInputMethod('manual');
    setOcrIdCardName(null);
    enterpriseForm.resetFields();
  };

  // 文件转Base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  // ==================== 个人认证相关 ====================

  // 个人身份证OCR识别
  const handleIdCardOcr = async (file) => {
    try {
      setPersonalIdCardOcrUploading(true);
      const base64 = await fileToBase64(file);
      const response = await recognizeIdCard(base64, 'front');

      if (!response?.data) {
        message.error('OCR服务无响应，请手动输入');
        return false;
      }

      const data = response.data;
      const { success, realName, idCardNo, confidence, error } = data;

      if (success && (realName || idCardNo)) {
        const fillData = {};
        if (realName?.trim()) fillData.realName = realName.trim();
        if (idCardNo?.trim()) fillData.idCardNo = idCardNo.trim();

        personalForm.setFieldsValue(fillData);
        message.success(`识别成功（置信度：${Math.round(confidence * 100)}%)`);
      }
      else if (realName || idCardNo) {
        const fillData = {};
        if (realName?.trim()) fillData.realName = realName.trim();
        if (idCardNo?.trim()) fillData.idCardNo = idCardNo.trim();

        personalForm.setFieldsValue(fillData);
        message.warning(`识别不完整，已自动填充部分信息（${realName ? '姓名' : ''}${realName && idCardNo ? '、' : ''}${idCardNo ? '身份证号' : ''}），请核对或补充`);
      }
      else {
        message.error(error || '识别失败，请手动输入');
      }
    } catch (error) {
      console.error('OCR识别失败:', error);
      message.error(error?.message || 'OCR识别失败，请手动输入');
    } finally {
      setPersonalIdCardOcrUploading(false);
    }
    return false;
  };

  // 发送验证码（个人）
  const handleSendCodePersonal = async () => {
    try {
      const phone = personalForm.getFieldValue('phoneNumber');
      if (!phone) {
        message.error('请输入手机号');
        return;
      }

      const res = await verificationService.sendVerificationCode(phone);
      const data = res?.data;

      if (data?.captchaRequired && !data?.sent) {
        setPendingPhone(phone);
        setCaptchaVisible(true);
        message.warning(res?.message || '需要完成人机验证');
        return;
      }

      message.success('验证码已发送');
      setCountdown(smsSendInterval);
    } catch (error) {
      message.error(error.message || '发送验证码失败');
    }
  };

  // 提交个人认证
  const handleSubmitPersonal = async (values) => {
    setPendingSubmitData(values);
    setPendingSubmitType('P');
    setConfirmModalVisible(true);
  };

  // ==================== 企业认证相关 ====================

  // 关键字搜索企业
  const handleSearchEnterprise = async () => {
    const keyword = searchKeyword.trim();
    if (keyword.length < 2) {
      message.error('搜索关键字至少需要2个字符');
      return;
    }

    try {
      setSearchLoading(true);
      const res = await searchEnterprise(keyword);
      if (res?.data) {
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length === 0) {
          message.warning('未找到匹配的企业，请尝试更精确的关键字');
        } else {
          setSearchResults(list);
          message.success(`找到${list.length}条企业信息`);
        }
      }
    } catch (error) {
      message.error(error.message || '搜索企业失败');
    } finally {
      setSearchLoading(false);
    }
  };

  // OCR识别营业执照后查询企业
  const handleBusinessLicenseOcrAndSearch = async (file) => {
    try {
      setBusinessLicenseOcrUploading(true);
      const base64 = await fileToBase64(file);
      const response = await recognizeBusinessLicense(base64);

      if (!response?.data) {
        message.error('OCR服务无响应，请手动输入');
        return false;
      }

      const data = response.data;
      const { success, companyName, socialCreditCode, legalPersonName, confidence, error } = data;

      if (!success) {
        message.error(error || 'OCR识别失败，请重试');
        return false;
      }

      try {
        setSearchLoading(true);
        const res = await queryEnterpriseByOcr({
          companyName: companyName || undefined,
          creditCode: socialCreditCode || undefined
        });

        if (res?.data) {
          setSelectedEnterprise(res.data);
          setEnterpriseStep(2);
          message.success(`识别并查询成功：${res.data.name}`);
        }
      } catch (error) {
        message.error(error.message || '查询企业信息失败');
      } finally {
        setSearchLoading(false);
      }
    } catch (error) {
      console.error('OCR识别失败:', error);
      message.error(error?.message || 'OCR识别失败，请重试');
    } finally {
      setBusinessLicenseOcrUploading(false);
    }
    return false;
  };

  // 选择企业进入第二步
  const handleSelectEnterprise = (enterprise) => {
    setSelectedEnterprise(enterprise);
    setEnterpriseStep(2);
    setSearchResults([]);
    setSearchKeyword('');
  };

  // 返回第一步
  const handleBackToStep1 = () => {
    setEnterpriseStep(1);
    setSelectedEnterprise(null);
    setIdCardInputMethod('manual');
    setOcrIdCardName(null);
    enterpriseForm.resetFields();
  };

  // 法定代表人身份证OCR识别
  const handleLegalPersonIdCardOcr = async (file) => {
    try {
      setLegalPersonIdCardOcrUploading(true);
      const base64 = await fileToBase64(file);
      const response = await recognizeIdCard(base64, 'front');

      if (!response?.data) {
        message.error('OCR服务无响应，请手动输入');
        return false;
      }

      const data = response.data;
      const { success, realName, idCardNo, confidence, error } = data;

      if (!success) {
        message.error(error || 'OCR识别失败，请重试');
        return false;
      }

      if (realName && selectedEnterprise?.operName && realName !== selectedEnterprise.operName) {
        message.error(`姓名不一致：身份证识别为"${realName}"，企业法定代表人为"${selectedEnterprise.operName}"，请上传正确的身份证`);
        return false;
      }

      if (idCardNo?.trim()) {
        enterpriseForm.setFieldsValue({ legalPersonIdCard: idCardNo.trim() });
      }
      if (realName?.trim()) {
        setOcrIdCardName(realName.trim());
      }

      message.success(`识别成功（置信度：${Math.round(confidence * 100)}%)`);
    } catch (error) {
      console.error('OCR识别失败:', error);
      message.error(error?.message || 'OCR识别失败，请重试');
    } finally {
      setLegalPersonIdCardOcrUploading(false);
    }
    return false;
  };

  // 发送验证码（企业）
  const handleSendCodeEnterprise = async () => {
    try {
      const phone = enterpriseForm.getFieldValue('contactPhone');
      if (!phone) {
        message.error('请输入法定代表人手机号');
        return;
      }

      const res = await verificationService.sendVerificationCode(phone);
      const data = res?.data;

      if (data?.captchaRequired && !data?.sent) {
        setPendingPhone(phone);
        setCaptchaVisible(true);
        message.warning(res?.message || '需要完成人机验证');
        return;
      }

      message.success('验证码已发送');
      setCountdown(smsSendInterval);
    } catch (error) {
      message.error(error.message || '发送验证码失败');
    }
  };

  // 提交企业认证
  const handleSubmitEnterprise = async (values) => {
    if (idCardInputMethod === 'ocr' && ocrIdCardName) {
      if (ocrIdCardName !== selectedEnterprise?.operName) {
        message.error(`姓名不一致：身份证识别为"${ocrIdCardName}"，企业法定代表人为"${selectedEnterprise.operName}"，请重新上传身份证`);
        return;
      }
    }

    const submitData = {
      companyName: selectedEnterprise.name,
      socialCreditCode: selectedEnterprise.creditCode,
      legalPersonName: selectedEnterprise.operName,
      legalPersonIdCard: values.legalPersonIdCard,
      contactPhone: values.contactPhone,
      verificationCode: values.verificationCode,
    };

    setPendingSubmitData(submitData);
    setPendingSubmitType('E');
    setConfirmModalVisible(true);
  };

  // 人机验证成功回调
  const handleCaptchaSuccess = useCallback(async (token) => {
    try {
      const res = await verificationService.sendVerificationCode(pendingPhone, token);
      const data = res?.data;

      if (data?.sent) {
        message.success('验证码已发送');
        setCountdown(smsSendInterval);
        setCaptchaVisible(false);
        setPendingPhone('');
      } else {
        message.error(data?.message || '发送验证码失败');
      }
    } catch (error) {
      message.error(error.message || '发送验证码失败');
    }
  }, [pendingPhone, smsSendInterval]);

  // 人机验证弹窗初始化
  useEffect(() => {
    if (!captchaVisible) return;

    let disposed = false;
    const timer = setTimeout(() => {
      if (disposed) return;
      tianaiCaptcha.show({
        bindEl: '#tianai-captcha-box',
        onValidSuccess: (token, tac) => {
          handleCaptchaSuccess(token);
          tac.destroyWindow();
          setCaptchaVisible(false);
        },
        onValidFail: () => {
          message.error('验证失败，请重试');
        }
      }).catch((error) => {
        message.error(error?.message || '验证码初始化失败');
        setCaptchaVisible(false);
      });
    }, 0);

    return () => {
      disposed = true;
      clearTimeout(timer);
      tianaiCaptcha.destroy();
    };
  }, [captchaVisible, handleCaptchaSuccess]);

  // 确认提交个人认证
  const handleConfirmPersonalSubmit = async () => {
    try {
      setLoading(true);
      const requestData = {
        verificationType: 'P',
        selectedMethod: 'phone',
        ...pendingSubmitData,
      };
      await verificationService.submitVerification(requestData);
      message.success('个人实名认证成功');
      setConfirmModalVisible(false);
      // 刷新认证列表，页面自动从 State A 切换到 State B
      await loadVerificationList();
    } catch (error) {
      message.error(error.message || '认证失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 确认提交企业认证
  const handleConfirmEnterpriseSubmit = async () => {
    try {
      setLoading(true);
      const requestData = {
        verificationType: 'E',
        selectedMethod: 'phone',
        ...pendingSubmitData,
      };
      await verificationService.submitVerification(requestData);
      message.success('企业实名认证成功');
      setConfirmModalVisible(false);
      // 折叠企业表单
      setShowEnterpriseForm(false);
      // 重置企业认证状态
      resetEnterpriseState();
      // 刷新认证列表
      await loadVerificationList();
    } catch (error) {
      message.error(error.message || '认证失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // ==================== 渲染函数 ====================

  // 个人认证表单（State A）
  const renderPersonalForm = () => (
    <Card className="card-compact" title="个人认证">
      <Alert
        message="请先完成个人实名认证"
        description="个人实名认证是使用平台服务的基础，完成后可进行企业认证。"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      <Form
        form={personalForm}
        layout={isMobile ? 'vertical' : 'horizontal'}
        onFinish={handleSubmitPersonal}
      >
        <Form.Item
          label="真实姓名"
          name="realName"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入真实姓名' },
            { pattern: /^[一-龥]+$/, message: '姓名只能包含中文' },
            { min: 2, max: 60, message: '姓名长度为2-60个字符' },
          ]}
        >
          <Input placeholder="请输入真实姓名" />
        </Form.Item>

        <Form.Item
          label="身份证号"
          name="idCardNo"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入身份证号' },
            {
              pattern: /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/,
              message: '请输入正确的身份证号',
            },
          ]}
          trigger="onChange"
          validateTrigger="onChange"
        >
          <Input
            placeholder="请输入身份证号"
            addonAfter={
              <Upload
                beforeUpload={handleIdCardOcr}
                accept="image/*"
                showUploadList={false}
                disabled={personalIdCardOcrUploading}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={personalIdCardOcrUploading}
                  type="text"
                >
                  {personalIdCardOcrUploading ? '识别中' : '识别'}
                </Button>
              </Upload>
            }
          />
        </Form.Item>

        <Form.Item
          label="手机号"
          name="phoneNumber"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
          ]}
        >
          <Input placeholder="请输入手机号" />
        </Form.Item>

        <Form.Item
          label="验证码"
          name="verificationCode"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入验证码' },
            { len: 6, message: '验证码为6位数字' },
          ]}
        >
          <Input
            placeholder="请输入验证码"
            addonAfter={
              <Button
                onClick={handleSendCodePersonal}
                disabled={countdown > 0}
              >
                {countdown > 0 ? `${countdown}s` : '发送验证码'}
              </Button>
            }
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: isMobile ? 0 : 6, span: isMobile ? 24 : 18 }}>
          <Space>
            <PermissionWrapper permission="API_VERIFICATION_SUBMIT">
              <Button type="primary" htmlType="submit" loading={loading}>
                提交认证
              </Button>
            </PermissionWrapper>
            <Button onClick={() => personalForm.resetFields()} disabled={loading}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );

  // 个人认证已验证卡片（State B/C）
  const renderPersonalVerifiedCard = () => (
    <Card className="card-compact verification-verified-card" title={
      <Space>
        <Text strong>个人认证</Text>
        <Tag color="green" icon={<CheckCircleOutlined />}>已认证</Tag>
      </Space>
    }>
      <Descriptions
        size="small"
        layout="horizontal"
        column={isMobile ? 1 : 2}
      >
        <Descriptions.Item label="真实姓名">{personalVerification?.realName || '-'}</Descriptions.Item>
        <Descriptions.Item label="身份证号">{personalVerification?.idCardNo || '-'}</Descriptions.Item>
        <Descriptions.Item label="认证时间" span={isMobile ? 1 : 2}>
          {personalVerification?.createTime
            ? dayjs(personalVerification.createTime).format('YYYY-MM-DD HH:mm')
            : '-'
          }
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // 已认证企业列表（State C）
  const renderEnterpriseList = () => {
    if (enterpriseVerifications.length === 0) return null;

    return (
      <div className="enterprise-verified-list" style={{ marginBottom: 16 }}>
        {enterpriseVerifications.map((item, index) => (
          <Card
            key={item.id || index}
            className="card-compact verification-verified-card"
            style={{ marginBottom: 12 }}
            title={
              <Space>
                <Text strong>{item.companyName}</Text>
                <Tag color="green" icon={<CheckCircleOutlined />}>已认证</Tag>
              </Space>
            }
          >
            <Descriptions
              size="small"
              layout="horizontal"
              column={1}
            >
              <Descriptions.Item label="统一社会信用代码">{item.socialCreditCode || '-'}</Descriptions.Item>
              <Descriptions.Item label="法定代表人姓名">{item.legalPersonName || '-'}</Descriptions.Item>
              <Descriptions.Item label="认证时间">
                {item.createTime
                  ? dayjs(item.createTime).format('YYYY-MM-DD HH:mm')
                  : '-'
                }
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ))}
      </div>
    );
  };

  // 企业认证区域（State B/C）
  const renderEnterpriseSection = () => (
    <div className="enterprise-section" style={{ marginTop: 16 }}>
      <Card className="card-compact" title="企业认证（可选）">
        {/* 已认证企业列表 */}
        {renderEnterpriseList()}

        {/* 空状态提示（仅无企业认证时显示） */}
        {enterpriseVerifications.length === 0 && !showEnterpriseForm && (
          <Empty
            description="暂无企业认证记录"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '16px 0' }}
          />
        )}

        {/* 添加/收起企业认证按钮 */}
        <Button
          type="dashed"
          icon={showEnterpriseForm ? <UpOutlined /> : <PlusOutlined />}
          onClick={() => {
            if (showEnterpriseForm) {
              resetEnterpriseState();
            }
            setShowEnterpriseForm(!showEnterpriseForm);
          }}
          block
          style={{ marginBottom: showEnterpriseForm ? 16 : 0 }}
        >
          {showEnterpriseForm ? '收起企业认证表单' : '添加企业认证'}
        </Button>

        {/* 企业认证表单（展开时显示） */}
        {showEnterpriseForm && (
          <div style={{ marginTop: 16 }}>
            <Divider style={{ margin: '0 0 16px 0' }} />
            {enterpriseStep === 1 ? renderEnterpriseStep1() : renderEnterpriseStep2()}
          </div>
        )}
      </Card>
    </div>
  );

  /**
   * 企业认证第一步：查询企业
   */
  const renderEnterpriseStep1 = () => (
    <div className="enterprise-step-container">
      <div className="step-indicator" style={{ marginBottom: 24 }}>
        <Space>
          <Tag color="blue">第1步</Tag>
          <Text strong>查询企业信息</Text>
        </Space>
      </div>

      {/* 方式1：关键字搜索 */}
      <Card size="small" title="方式1：关键字搜索" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="请输入企业名称关键字（至少2个字）"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearchEnterprise}
            disabled={searchLoading}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearchEnterprise}
            loading={searchLoading}
          >
            查询
          </Button>
        </Space.Compact>
      </Card>

      {/* 方式2：OCR识别 */}
      <Card size="small" title="方式2：上传营业执照OCR识别" style={{ marginBottom: 16 }}>
        <Upload
          beforeUpload={handleBusinessLicenseOcrAndSearch}
          accept="image/*"
          showUploadList={false}
          disabled={businessLicenseOcrUploading || searchLoading}
        >
          <Button
            icon={<UploadOutlined />}
            loading={businessLicenseOcrUploading}
            disabled={searchLoading}
            block
          >
            {businessLicenseOcrUploading ? '识别中...' : '点击上传营业执照图片'}
          </Button>
        </Upload>
        <Text type="secondary" style={{ fontSize: 12 }}>
          支持jpg、png格式，上传后自动识别并查询企业信息
        </Text>
      </Card>

      {/* 搜索结果列表 */}
      {searchResults.length > 0 && (
        <Card size="small" title="查询结果（点击选择）" style={{ marginBottom: 16 }}>
          <List
            dataSource={searchResults}
            renderItem={(item, index) => (
              <List.Item
                key={index}
                onClick={() => handleSelectEnterprise(item)}
                style={{ cursor: 'pointer', padding: '12px 0' }}
                className="enterprise-search-result-item"
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{item.name}</Text>
                      {item.status === '在业' && <Tag color="green" style={{ fontSize: 12 }}>{item.status}</Tag>}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text type="secondary">法定代表人：{item.operName}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>信用代码：{item.creditCode}</Text>
                    </Space>
                  }
                />
                <Button type="primary" size="small">
                  选择
                </Button>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );

  /**
   * 企业认证第二步：法定代表人信息验证
   */
  const renderEnterpriseStep2 = () => (
    <div className="enterprise-step-container">
      {/* 返回按钮 */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={handleBackToStep1}
        style={{ marginBottom: 16 }}
      >
        返回重新选择企业
      </Button>

      <div className="step-indicator" style={{ marginBottom: 16 }}>
        <Space>
          <Tag color="blue">第2步</Tag>
          <Text strong>法定代表人信息验证</Text>
        </Space>
      </div>

      {/* 企业信息展示（只读） */}
      <Card
        size="small"
        title={<Space><Text strong>企业信息</Text></Space>}
        style={{ marginBottom: 16 }}
      >
        <Descriptions size="small" column={isMobile ? 1 : 2}>
          <Descriptions.Item label="企业名称" span={2}>{selectedEnterprise?.name}</Descriptions.Item>
          <Descriptions.Item label="统一社会信用代码" span={2}>{selectedEnterprise?.creditCode}</Descriptions.Item>
          <Descriptions.Item label="法定代表人">{selectedEnterprise?.operName}</Descriptions.Item>
          <Descriptions.Item label="企业状态">{selectedEnterprise?.status}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 法定代表人身份信息表单 */}
      <Form
        form={enterpriseForm}
        layout={isMobile ? 'vertical' : 'horizontal'}
        onFinish={handleSubmitEnterprise}
      >
        {/* 身份证录入方式选择 */}
        <Form.Item label="身份证录入方式" required>
          <Radio.Group
            value={idCardInputMethod}
            onChange={(e) => {
              setIdCardInputMethod(e.target.value);
              setOcrIdCardName(null);
              enterpriseForm.setFieldsValue({ legalPersonIdCard: undefined });
            }}
          >
            <Radio value="manual">手动输入</Radio>
            <Radio value="ocr">OCR识别</Radio>
          </Radio.Group>
        </Form.Item>

        {/* 法定代表人身份证号 */}
        <Form.Item
          label="法定代表人身份证号"
          name="legalPersonIdCard"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入法定代表人身份证号' },
            {
              pattern: /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/,
              message: '请输入正确的法定代表人身份证号',
            },
          ]}
          trigger="onChange"
          validateTrigger="onChange"
        >
          {idCardInputMethod === 'ocr' ? (
            <Input
              placeholder="请上传法定代表人身份证照片"
              value={enterpriseForm.getFieldValue('legalPersonIdCard')}
              readOnly
              addonAfter={
                <Upload
                  beforeUpload={handleLegalPersonIdCardOcr}
                  accept="image/*"
                  showUploadList={false}
                  disabled={legalPersonIdCardOcrUploading}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={legalPersonIdCardOcrUploading}
                    type="text"
                  >
                    {legalPersonIdCardOcrUploading ? '识别中' : '上传识别'}
                  </Button>
                </Upload>
              }
            />
          ) : (
            <Input placeholder="请输入法定代表人身份证号" />
          )}
        </Form.Item>

        {/* OCR方式下的提示信息 */}
        {idCardInputMethod === 'ocr' && ocrIdCardName && (
          <Alert
            message={`已识别姓名：${ocrIdCardName}`}
            description={`将使用企业法定代表人"${selectedEnterprise?.operName}"进行身份认证`}
            type="info"
            showIcon
            style={{ marginBottom: 16, marginLeft: isMobile ? 0 : '25%' }}
          />
        )}

        {/* 手动输入方式下的提示信息 */}
        {idCardInputMethod === 'manual' && (
          <Alert
            message={`将使用企业法定代表人"${selectedEnterprise?.operName}"进行身份认证`}
            type="info"
            showIcon
            style={{ marginBottom: 16, marginLeft: isMobile ? 0 : '25%' }}
          />
        )}

        <Form.Item
          label="法定代表人手机号"
          name="contactPhone"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入法定代表人手机号' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
          ]}
        >
          <Input placeholder="请输入法定代表人手机号" />
        </Form.Item>

        <Form.Item
          label="验证码"
          name="verificationCode"
          labelCol={{ span: isMobile ? undefined : 6 }}
          wrapperCol={{ span: isMobile ? undefined : 18 }}
          rules={[
            { required: true, message: '请输入验证码' },
            { len: 6, message: '验证码为6位数字' },
          ]}
        >
          <Input
            placeholder="请输入验证码"
            addonAfter={
              <Button
                onClick={handleSendCodeEnterprise}
                disabled={countdown > 0}
              >
                {countdown > 0 ? `${countdown}s` : '发送验证码'}
              </Button>
            }
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: isMobile ? 0 : 6, span: isMobile ? 24 : 18 }}>
          <Space>
            <PermissionWrapper permission="API_VERIFICATION_SUBMIT">
              <Button type="primary" htmlType="submit" loading={loading}>
                提交认证
              </Button>
            </PermissionWrapper>
            <Button onClick={handleBackToStep1} disabled={loading}>
              返回上一步
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );

  // ==================== 主渲染 ====================

  return (
    <div className="page-container p-identity-verification">
      <div className="page-content">
        <h1 className="page-title">实名认证</h1>

        {verificationListLoading ? (
          <Card loading={true} className="card-compact" />
        ) : (
          <>
            {/* 个人认证区域 */}
            {personalVerification
              ? renderPersonalVerifiedCard()
              : renderPersonalForm()
            }

            {/* 企业认证区域 - 仅在个人认证完成后显示 */}
            {personalVerification && renderEnterpriseSection()}
          </>
        )}
      </div>

      {/* 人机验证弹窗 */}
      <Modal
        open={captchaVisible}
        title="安全验证"
        onCancel={() => setCaptchaVisible(false)}
        footer={null}
        centered
      >
        <div id="tianai-captcha-box" style={{ minWidth: 318, minHeight: 318 }} />
      </Modal>

      {/* 提交确认弹窗 */}
      <Modal
        title="确认提交认证信息"
        open={confirmModalVisible}
        onOk={pendingSubmitType === 'P' ? handleConfirmPersonalSubmit : handleConfirmEnterpriseSubmit}
        onCancel={() => setConfirmModalVisible(false)}
        okText="确认提交"
        cancelText="返回修改"
        centered
      >
        {pendingSubmitData && pendingSubmitType === 'P' && (
          <Descriptions
            bordered
            size="small"
            column={1}
            items={[
              { label: '认证类型', children: '个人认证' },
              { label: '真实姓名', children: pendingSubmitData.realName },
              { label: '身份证号', children: pendingSubmitData.idCardNo },
              { label: '手机号', children: pendingSubmitData.phoneNumber },
              { label: '认证方式', children: '手机号验证' },
            ]}
          />
        )}
        {pendingSubmitData && pendingSubmitType === 'E' && (
          <Descriptions
            bordered
            size="small"
            column={1}
            items={[
              { label: '认证类型', children: '企业认证' },
              { label: '企业名称', children: pendingSubmitData.companyName },
              { label: '统一社会信用代码', children: pendingSubmitData.socialCreditCode },
              { label: '法定代表人姓名', children: pendingSubmitData.legalPersonName },
              { label: '法定代表人身份证号', children: pendingSubmitData.legalPersonIdCard },
              { label: '法定代表人手机号', children: pendingSubmitData.contactPhone },
              { label: '认证方式', children: '手机号验证' },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default IdentityVerificationPage;
